import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

import { pickInheritedCommission, SiteRateScheduleDTO } from './SiteRatesForm';
import { resolveUseVendorRateForDriver } from './vendorRateForDriver';

export interface SiteRateAddOnAssignments {
  customerAddOns: AddOnAssignmentSchema[];
  vendorAddOns: AddOnAssignmentSchema[];
  driverAddOns: AddOnAssignmentSchema[];
}

export interface ConvertSiteRateScheduleOptions {
  // Fetches add-on assignments for a saved SRS by id.
  fetchAddOnAssignments: (
    siteRateScheduleId: string,
  ) => Promise<SiteRateAddOnAssignments>;
  // Copy-and-edit strips SRS ids and marks add-ons new so the
  // backend creates fresh records instead of patching the source's.
  isCopyAndEdit: boolean;
  stripSrsIds: boolean;
  // `default_vendor_rate_to_driver` treatment.
  defaultVendorRateToDriverFeatureFlagEnabled: boolean;
}

// Converts a single `SiteRateSchedule` model into the form DTO used by the
// rate-card UI. Extracted from `FreightRates.convertToSiteRateDTO` so the
// add-on fetch ordering and the `useVendorRateForDriver` default can be unit
// tested directly.
//
// TRE-15083: `useVendorRateForDriver` is resolved AFTER add-ons are populated,
// so a migrated card with a driver add-on (but null legacy driver rate) counts
// as an explicit driver setup and the box stays OFF — saving it otherwise wipes
// the driver add-on and mirrors the vendor rate.
export const convertSiteRateScheduleToDTO = async (
  rate: SiteRateSchedule,
  {
    fetchAddOnAssignments,
    isCopyAndEdit,
    stripSrsIds,
    defaultVendorRateToDriverFeatureFlagEnabled,
  }: ConvertSiteRateScheduleOptions,
): Promise<SiteRateScheduleDTO> => {
  // CRITICAL: In copy mode, strip IDs so site rates are created as new records
  // instead of updating the original project's site rate schedules.
  // TRE-14507: only strip when the caller opts in via `stripSrsIds` —
  // copy-and-editing an order whose SRS come from the parent project must keep
  // the IDs (otherwise the project gets duplicate records).
  // Reuse the server id as the clientKey so the resilient save flow
  // can correlate operation results back to this row even before any save
  // attempt.
  const persistedId = isCopyAndEdit && stripSrsIds ? undefined : rate.id;
  const baseDTO: SiteRateScheduleDTO = {
    isDeleted: false,
    id: persistedId,
    clientKey: rate.id ?? crypto.randomUUID(),
    name: rate.name ?? null,
    pickupSite: rate.pickupSite
      ? { label: rate.pickupSite.name, value: rate.pickupSite.id }
      : null,
    dropoffSite: rate.dropoffSite
      ? { label: rate.dropoffSite.name, value: rate.dropoffSite.id }
      : null,
    customerRate:
      (rate.customerRateValue !== null && rate.customerRateValue !== undefined) ||
      rate.customerRateType
        ? {
            rate: rate.customerRateValue ?? undefined,
            type: rate.customerRateType
              ? { label: rate.customerRateType, value: rate.customerRateType }
              : null,
          }
        : null,
    vendorPayCommissionPercentage: rate.vendorPayCommissionPercentage,
    driverPayCommissionPercentage: rate.driverPayCommissionPercentage,
    ...pickInheritedCommission(rate),
    netVendorRate: rate.netVendorRate,
    netDriverRate: rate.netDriverRate,
    vendorRate:
      (rate.vendorRateValue !== null && rate.vendorRateValue !== undefined) ||
      rate.vendorRateType
        ? {
            rate: rate.vendorRateValue ?? undefined,
            type: rate.vendorRateType
              ? { label: rate.vendorRateType, value: rate.vendorRateType }
              : null,
          }
        : null,
    driverRate:
      (rate.driverRateValue !== null && rate.driverRateValue !== undefined) ||
      rate.driverRateType
        ? {
            rate: rate.driverRateValue ?? undefined,
            type: rate.driverRateType
              ? { label: rate.driverRateType, value: rate.driverRateType }
              : null,
          }
        : null,
    customerAddOns: [],
    vendorAddOns: [],
    driverAddOns: [],
    material: rate.material
      ? { label: rate.material.name, value: rate.material.id }
      : null,
    equipmentType: rate.equipmentType
      ? { label: rate.equipmentType.name, value: rate.equipmentType.id }
      : null,
    useVendorRateForDriver: undefined,
    jurisdiction: rate.jurisdiction ?? null,
    quantity: rate.quantity ?? undefined,
    pwOnSiteRate: rate.pwOnSiteRate ?? undefined,
    totalFringeRate: rate.totalFringeRate ?? undefined,
  };

  // Fetch add-on assignments if the site rate has an ID
  if (rate.id) {
    try {
      const addOns = await fetchAddOnAssignments(rate.id);
      // In copy mode (and when the caller opts in to recreating the SRS —
      // see the `stripSrsIds` check above), mark add-ons as new so they are
      // created alongside the new SRS instead of patched on the source's
      // records.
      if (isCopyAndEdit && stripSrsIds) {
        baseDTO.customerAddOns = addOns.customerAddOns.map((a) => ({
          ...a,
          isNew: true,
        }));
        baseDTO.vendorAddOns = addOns.vendorAddOns.map((a) => ({
          ...a,
          isNew: true,
        }));
        baseDTO.driverAddOns = addOns.driverAddOns.map((a) => ({
          ...a,
          isNew: true,
        }));
      } else {
        baseDTO.customerAddOns = addOns.customerAddOns;
        baseDTO.vendorAddOns = addOns.vendorAddOns;
        baseDTO.driverAddOns = addOns.driverAddOns;
      }
    } catch (error) {
      console.error(`Failed to fetch add-ons for site rate ${rate.id}:`, error);
    }
  }

  // Resolve the default after add-ons are populated (see header note).
  baseDTO.useVendorRateForDriver = resolveUseVendorRateForDriver(
    baseDTO,
    defaultVendorRateToDriverFeatureFlagEnabled,
  );

  return baseDTO;
};
